import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { SlideOutSheet, PageLink } from '.';
import { formatCurrency, isNullOrUndefined } from '@shared';
import { careEnvironmentDefs } from '@shared';
import { CustomTooltip } from './CustomTooltip';
import { Badge } from '@/components/ui/badge';
import { AiFillCheckCircle } from 'react-icons/ai';
import { GoDot } from 'react-icons/go';
import { Clock4, DollarSign, MapPinIcon, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCareSettingsPageBy } from '@/util/navigationLogic';
import { Button } from './ui/button';

interface PhaseCardProps {
  phase: CarePhaseDataItem;
  carePhase: CarePhase;
  clientId: string;
  clientHasStartedLtc: boolean;
  clientCurrentCarePhase: CarePhase | null;
  client: Client;
  timelineText: string;
  timelineTooltip: string;
}

export interface CarePhaseDataItem {
  value: CarePhase;
  carePhase: string;
  adlRange: string;
  careSettingOptions: string;
  adlRangeIcon: React.ReactElement;
  slideOutContent: React.ReactElement;
  phaseStartAge: number | null;
  phaseCareMonthsNeeded: number;
  selectedCareEnvironment: CareEnvironment | null;
  phaseInflatedProfessionalShareCost: number;
  isDurationCustom: boolean;
}

export function PhaseCard({
  phase,
  carePhase,
  clientId,
  clientHasStartedLtc,
  clientCurrentCarePhase,
  client,
  timelineText,
  timelineTooltip,
}: PhaseCardProps) {
  const isPhaseActive =
    phase.value === carePhase || !isNullOrUndefined(phase.selectedCareEnvironment);

  const phaseLink = isPhaseActive ? getCareSettingsPageBy(phase.value) : null;

  return (
    <div className="group relative">
      <Badge
        variant={phase.value <= carePhase ? 'default' : 'secondary'}
        className={cn(
          'absolute -top-3 left-7 z-10 flex items-center gap-1.5 px-3 py-1',
          phase.value < carePhase ? 'bg-green-100 text-green-500 hover:bg-green-100 ' : '',
          phase.value === carePhase ? 'bg-primary hover:bg-primary text-white' : '',
        )}
      >
        <div
          className={cn('flex items-center gap-1', phase.value <= carePhase ? '' : ' opacity-40')}
        >
          {phase.value < carePhase ? (
            <AiFillCheckCircle className="h-4 w-4" />
          ) : (
            <GoDot className="h-4 w-4" />
          )}
          <CustomTooltip
            content={timelineTooltip}
            className="text-sm"
          >
            <span>{timelineText}</span>
          </CustomTooltip>
        </div>
      </Badge>

      <div
        key={phase.carePhase}
        className={`border-primary relative rounded-lg border bg-white px-7 pb-3 pt-7 text-gray-800 shadow-sm md:px-4 lg:pb-7 lg:pt-10 ${
          carePhase === phase.value ? '' : 'opacity-40'
        }`}
      >
        <dl className="flex flex-col gap-1">
          <dt className="flex items-center text-xl font-semibold md:text-lg lg:text-2xl">
            {phaseLink ? (
              <PageLink
                to={phaseLink}
                targetProps={{ clientId }}
              >
                {phase.carePhase}
              </PageLink>
            ) : (
              <button className="hover:cursor-default">{phase.carePhase}</button>
            )}
            <SlideOutSheet
              slideOutContent={phase.slideOutContent}
              title="FAQ"
            >
              <BsFillQuestionCircleFill
                className="h-[18px] w-[18px] pb-1 pl-1  text-gray-400 hover:text-gray-500"
                aria-hidden="true"
              />
            </SlideOutSheet>
          </dt>
          <dd className="mt-1 flex text-base leading-7 text-gray-700">
            <ul>
              <li className="mt-1 flex gap-3">
                <CustomTooltip
                  triggerAsChild={false}
                  content="Self-care activities include bathing, dressing, eating, transferrring (e.g.,from bed to chair), toileting, and continence."
                >
                  <>
                    {phase.adlRangeIcon}
                    <div className="text-start text-base md:text-sm lg:text-base">
                      {phase.adlRange} self-care activities
                    </div>
                  </>
                </CustomTooltip>
              </li>

              <li className="mt-2 flex gap-3">
                <CustomTooltip
                  triggerAsChild={false}
                  content={
                    phase.isDurationCustom
                      ? 'Custom Duration'
                      : `Estimated ${clientHasStartedLtc && phase.value === clientCurrentCarePhase ? 'remaining ' : ''}duration`
                  }
                >
                  <>
                    <Clock4 className="h-4 w-4" />
                    <div className="text-start text-base md:text-sm lg:text-base">
                      {phase.phaseCareMonthsNeeded} months
                    </div>
                  </>
                </CustomTooltip>
              </li>

              <li className="mt-2 flex gap-3">
                <CustomTooltip
                  triggerAsChild={false}
                  content="Selected care setting"
                >
                  <>
                    <MapPinIcon className="h-4 w-4" />
                    <div className="text-start text-base md:text-sm lg:text-base">
                      {phase.selectedCareEnvironment
                        ? `${careEnvironmentDefs[phase.selectedCareEnvironment].label}`
                        : '---'}
                    </div>
                  </>
                </CustomTooltip>
              </li>

              <li className="mt-2 flex gap-3">
                <CustomTooltip
                  triggerAsChild={false}
                  content="Estimated cost"
                >
                  <>
                    <DollarSign className="h-4 w-4" />
                    <div className="text-start text-base md:text-sm lg:text-base">
                      {phase.selectedCareEnvironment
                        ? `${formatCurrency(phase.phaseInflatedProfessionalShareCost).slice(1)}`
                        : '---'}
                    </div>
                  </>
                </CustomTooltip>
              </li>
            </ul>
          </dd>
        </dl>
        {phaseLink ? (
          <div className="bg-primary absolute right-4 top-4 rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <PageLink
              to={phaseLink}
              targetProps={{ clientId }}
            >
              <Button
                size="sm"
                className="h-8 w-8 rounded-full p-0"
              >
                <ArrowUpRight className="h-4 w-4 text-white" />
              </Button>
            </PageLink>
          </div>
        ) : null}
      </div>
    </div>
  );
}
